<template>
  <div class="billing-address-screen">
    <!-- Loading State -->
    <div v-if="isLoadingAddresses" class="loading-message">
      {{ t('labels.Loading') }}...
    </div>

    <!-- Address Selection View -->
    <div v-else-if="showAddressSelection" class="address-selection-view">
      <div class="selection-header">
        <button @click="cancelAddressSelection" class="back-button">
          <FontAwesomeIcon :icon="faArrowLeft" />
          {{ t('buttons.Back') }}
        </button>
        <h3>{{ t('accountInfo.billingAddress.selectAddress') }} {{ addressSelectionType === 'billing' ? t('accountInfo.billingAddress.billing') : t('accountInfo.billingAddress.shipping') }}</h3>
      </div>

      <div class="address-list">
        <div
          v-for="address in sortedAddressList"
          :key="address.addressId"
          class="address-item"
          :class="{ 'is-default': isDefaultAddress(address) }"
        >
          <div class="address-content" @click="selectExistingAddress(address)">
            <div class="address-name">
              {{ address.firstName }} {{ address.lastName }}
              <span v-if="isDefaultAddress(address)" class="default-badge">
                {{ t('accountInfo.billingAddress.defaultBadge') }}
              </span>
            </div>
            <div class="address-line">
              {{ address.addressLine1 }}
              <span v-if="address.houseNumber"> {{ address.houseNumber }}</span>
            </div>
            <div v-if="address.addressLine2" class="address-line">{{ address.addressLine2 }}</div>
            <div class="address-line">{{ address.city }}<template v-if="address.stateCode && address.stateCode !== 'N/A'">, {{ address.stateCode }}</template> {{ address.postalCode }}</div>
            <div class="address-line">{{ address.countryCode }}</div>
          </div>
          <button
            class="delete-address-button"
            @click.stop="deleteAddress(address)"
            :title="t('accountInfo.billingAddress.deleteAddress')"
          >
            <FontAwesomeIcon :icon="faTrash" />
          </button>
        </div>

        <div class="address-item add-new" @click="addNewAddress">
          <div class="add-new-content">
            <FontAwesomeIcon :icon="faPlus" class="add-icon" />
            <span>{{ t('accountInfo.billingAddress.addNewAddress') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Address Form View -->
    <div v-else-if="showBillingAddressForm" class="address-form-view">
      <div class="form-header">
        <button @click="cancelBillingAddressForm" class="back-button">
          <FontAwesomeIcon :icon="faArrowLeft" />
          {{ t('buttons.Back') }}
        </button>
        <h3>{{ editingBillingAddress ? t('accountInfo.billingAddress.editAddress') : t('accountInfo.billingAddress.addNewAddress') }}</h3>
      </div>

      <AddressControl
        ref="addressControlRef"
        :name="billingAddressNameDisplay"
        :addressLine1="billingAddressForm.address1"
        :addressLine2="billingAddressForm.address2"
        :houseNumber="billingAddressForm.houseNumber"
        :city="billingAddressForm.city"
        :stateCode="billingAddressForm.state"
        :postalCode="billingAddressForm.postalCode"
        :countryCode="billingAddressForm.country"
        :columnClass="'col-12'"
        :isdisabled="false"
        @address-change="onBillingAddressChange"
      />

      <div class="form-actions">
        <button @click="cancelBillingAddressForm" class="form-button secondary">
          {{ t('buttons.Cancel') }}
        </button>
        <button @click="saveBillingAddress(addressControlRef)" class="form-button primary">
          {{ t('buttons.Save') }}
        </button>
      </div>
    </div>

    <!-- Main Address Management View -->
    <div v-else class="address-management-view">
      <!-- Current Addresses Display -->
      <div class="current-addresses">
        <!-- Billing Address -->
        <div class="address-section">
          <h3 class="address-section-title">{{ t('accountInfo.billingAddress.currentBilling') }}</h3>
          <div v-if="billingAddress" class="address-display">
            <div class="address-content">
              <div class="address-name">{{ billingAddress.firstName }} {{ billingAddress.lastName }}</div>
              <div class="address-line">
                {{ billingAddress.addressLine1 }}
                <span v-if="billingAddress.houseNumber"> {{ billingAddress.houseNumber }}</span>
              </div>
              <div v-if="billingAddress.addressLine2" class="address-line">{{ billingAddress.addressLine2 }}</div>
              <div class="address-line">{{ billingAddress.city }}<template v-if="billingAddress.stateCode && billingAddress.stateCode !== 'N/A'">, {{ billingAddress.stateCode }}</template> {{ billingAddress.postalCode }}</div>
              <div class="address-line">{{ billingAddress.countryCode }}</div>
            </div>
            <div class="address-actions">
              <button @click="changeAddress('billing')" class="change-button">
                {{ t('accountInfo.billingAddress.change') }}
              </button>
              <button @click="addNewAddress" class="change-button secondary">
                {{ t('accountInfo.billingAddress.createNew') }}
              </button>
            </div>
          </div>
          <div v-else class="no-address">
            <p>{{ t('accountInfo.billingAddress.noBillingAddress') }}</p>
            <button @click="changeAddress('billing')" class="form-button primary">
              {{ t('accountInfo.billingAddress.addBillingAddress') }}
            </button>
          </div>
        </div>

        <!-- Shipping Address -->
        <div class="address-section">
          <h3 class="address-section-title">{{ t('accountInfo.billingAddress.currentShipping') }}</h3>
          <div v-if="shippingAddress" class="address-display">
            <div class="address-content">
              <div class="address-name">{{ shippingAddress.firstName }} {{ shippingAddress.lastName }}</div>
              <div class="address-line">
                {{ shippingAddress.addressLine1 }}
                <span v-if="shippingAddress.houseNumber"> {{ shippingAddress.houseNumber }}</span>
              </div>
              <div v-if="shippingAddress.addressLine2" class="address-line">{{ shippingAddress.addressLine2 }}</div>
              <div class="address-line">{{ shippingAddress.city }}<template v-if="shippingAddress.stateCode && shippingAddress.stateCode !== 'N/A'">, {{ shippingAddress.stateCode }}</template> {{ shippingAddress.postalCode }}</div>
              <div class="address-line">{{ shippingAddress.countryCode }}</div>
            </div>
            <div class="address-actions">
              <button @click="changeAddress('shipping')" class="change-button">
                {{ t('accountInfo.billingAddress.change') }}
              </button>
              <button @click="addNewAddress" class="change-button secondary">
                {{ t('accountInfo.billingAddress.createNew') }}
              </button>
            </div>
          </div>
          <div v-else class="no-address">
            <p>{{ t('accountInfo.billingAddress.noShippingAddress') }}</p>
            <button @click="changeAddress('shipping')" class="form-button primary">
              {{ t('accountInfo.billingAddress.addShippingAddress') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Address Verification Popup -->
    <div v-if="addressVerificationShow" class="popup-backdrop"></div>
    <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
      <VerifyAddressPopup
        :enteredAddress="{
          addressLine1: billingAddressForm.address1,
          addressLine2: billingAddressForm.address2,
          houseNumber: billingAddressForm.houseNumber,
          city: billingAddressForm.city,
          stateCode: billingAddressForm.state,
          postalCode: billingAddressForm.postalCode,
          countryCode: billingAddressForm.country,
          firstName: billingAddressForm.firstName,
          lastName: billingAddressForm.lastName
        }"
        :verifyAddress="verifyAddress"
        :verifyAddressError="addressVerifyError"
        @use-entered-address="useEnteredAddressClick"
        @use-selected-address="useSelectedAddressClick"
        @re-enter-address="reEnterAddressClick">
      </VerifyAddressPopup>
    </Popup>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { Popup } from '@progress/kendo-vue-popup'
import AddressControl from './AddressControl.vue'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import { useBillingAddress } from '../../composables/useBillingAddress'

const { t } = useI18n()
const addressControlRef = ref<InstanceType<typeof AddressControl> | null>(null)

const {
  // State
  showAddressSelection,
  addressSelectionType,
  showBillingAddressForm,
  editingBillingAddress,
  addressVerificationShow,
  verifyAddress,
  addressVerifyError,
  sortedAddressList,
  isDefaultAddress,
  billingAddress,
  shippingAddress,
  isLoadingAddresses,
  // countries not needed - AddressControl handles internally
  billingAddressForm,
  billingAddressNameDisplay,

  // Methods
  changeAddress,
  addNewAddress,
  selectExistingAddress,
  cancelAddressSelection,
  useEnteredAddressClick,
  useSelectedAddressClick,
  reEnterAddressClick,
  onBillingAddressChange,
  saveBillingAddress,
  cancelBillingAddressForm,
  loadBillingAddressData,
  deleteAddress
} = useBillingAddress()

onMounted(async () => {
  await loadBillingAddressData()
})
</script>

<style lang="scss" scoped>
.billing-address-screen {
  width: 100%;
}

.loading-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.selection-header,
.form-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: 24px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  padding: $spacing-2 $spacing-4;
  background: white;
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-md;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
}

.address-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $gap-md;
}

.address-content {
  flex: 1;
  cursor: pointer;
}

.address-content:hover {
  opacity: 0.8;
}

.address-item:hover {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 2px 8px rgba(black, 0.1);
}

.address-item.is-default {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  background-color: var(--vt-c-white-soft);
}

.delete-address-button {
  flex-shrink: 0;
  padding: $spacing-2;
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.delete-address-button:hover {
  background: #dc3545;
  color: white;
  transform: scale(1.05);
}

.address-item.add-new {
  border: 2px dashed #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  cursor: pointer;
}

.address-item.add-new:hover {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  background-color: var(--vt-c-white-soft);
}

.add-new-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gap-sm;
  color: #666;
}

.add-icon {
  @include typography-h3;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-content {
  line-height: 1.5;
}

.address-name {
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 4px;
}

.address-line {
  @include typography-small;
  color: #666;
}

.current-addresses {
  display: flex;
  flex-direction: column;
  gap: $spacing-6;
}

.address-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $spacing-6;
}

.address-section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 16px 0;
}

.address-display {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $gap-md;
}

.address-actions {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
  flex-shrink: 0;
}

.change-button {
  padding: $spacing-2 $spacing-4;
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
}

.change-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.change-button.secondary {
  background: white;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border: 1px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.change-button.secondary:hover {
  background-color: var(--vt-c-white-soft);
}

.no-address {
  text-align: center;
  padding: $card-padding;
}

.no-address p {
  color: #666;
  margin: 0 0 16px 0;
}

.form-actions {
  display: flex;
  gap: $spacing-3;
  justify-content: flex-end;
  margin-top: 24px;
}

.form-button {
  padding: $spacing-3 $spacing-6;
  border: none;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-button.primary {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.form-button.primary:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.form-button.secondary {
  background-color: var(--vt-c-white-soft);
  color: #666;
  border: 1px solid #e0e0e0;
}

.form-button.secondary:hover {
  background-color: #e9ecef;
}

/* Billing Address Specific Styles */
.billing-address-section {
  padding: $card-padding;
}

.address-display-section {
  margin-bottom: 2rem;
}

.address-type-section {
  margin-bottom: 2rem;
}

.address-type-section h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-selection-view {
  width: 100%;
}

.selection-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: 20px;
}

.selection-header h3 {
  @include typography-h4;
  margin: 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-list h4 {
  @include font-weight-semibold;
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  background-color: var(--vt-c-white-soft);
  margin-bottom: 1rem;
  color: #000;
}

.address-item:last-child {
  margin-bottom: 0;
}

.address-item.selectable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.address-item.selectable:hover {
  background-color: #e9f5f7;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(var(--vt-c-teal-dark), 0.15);
}

.no-addresses {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  background-color: var(--vt-c-white-soft);
}

.add-new-section {
  margin-top: 1rem;
  text-align: center;
}

.default-badge {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  padding: $spacing-1 $spacing-2;
  @include typography-caption;
  @include font-weight-medium;
  border-radius: $radius-lg;
  margin-left: 8px;
}

.loading-message {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
}

.address-display {
  flex: 1;
}

.address-display p {
  margin: 0;
  line-height: 1.5;
}

.no-address-text {
  color: #666;
  font-style: italic;
}

.address-actions {
  display: flex;
  gap: $gap-sm;
  margin-left: 1rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.address-form {
  padding: 1rem;
  border: 1px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-radius: $radius-md;
  background-color: #fefefe;
}

.address-form h5 {
  margin-bottom: 1rem;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.address-control {
  margin-bottom: 1rem;
}

.form-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-button:disabled:hover {
  background-color: #6c757d;
  transform: none;
}

.popup-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(black, 0.5);
  z-index: $z-modal-backdrop;
  backdrop-filter: blur(2px);
}

@media (max-width: 768px) {
  .current-addresses {
    grid-template-columns: 1fr;
  }

  .billing-address-section {
    padding: $spacing-4;
  }

  .address-actions {
    flex-direction: column;
    gap: $gap-sm;
  }

  .selection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-3;
  }
}
</style>
